import React, { useEffect, useState } from "react";

// Semantic UI React
import {
  Icon,
  Segment,
  Sidebar,
  Button,
  Checkbox,
  List,
  Dropdown,
  Divider,
  Header,
} from "semantic-ui-react";
import ReactGA from "react-ga4";

// Styling
import "semantic-ui-css/semantic.min.css";
import "./Style.css";

// ESRI
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Extent from "@arcgis/core/geometry/Extent";
import esriId from "@arcgis/core/identity/IdentityManager";
import * as query from "@arcgis/core/rest/query.js";
import Query from "@arcgis/core/rest/support/Query.js";
import TileLayer from "@arcgis/core/layers/TileLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer.js";
import Point from "@arcgis/core/geometry/Point.js";

// types
import type Graphic from "@arcgis/core/Graphic";
import type Feature from "@arcgis/core/widgets/Feature.js";

// Widgets
// import ZoomWidget from "./js/ZoomWidget";

var default_extent = new Extent({
  xmin: 2267399.49061241,
  ymin: 290417.14268965,
  xmax: 2322633.86561241,
  ymax: 316940.58018965,
  spatialReference: {
    wkid: 3418,
  },
});
// console.log({ config });

// Create map
const MapApp = ({ config }) => {
  const [view, setView] = useState<MapView | null>(null);
  // Project Areas
  const [projectAreaLayer, setProjectAreaLayer] = useState<FeatureLayer | null>(
    null
  );
  const [projectAreas, setProjectAreas] = useState<Graphic[]>([]);
  const [selProjectAreas, setSelProjectAreas] = useState<Graphic[]>([]);
  const [projectAreaVisible, setProjectAreaVisible] = useState<Graphic[]>([]);

  // Project Sites
  const [projectSiteLayer, setProjectSiteLayer] = useState<FeatureLayer | null>(
    null
  );
  const [projectSites, setProjectSites] = useState<Graphic[]>([]);
  const [selProjectSites, setSelprojectSites] = useState<Graphic[]>([]);
  const [projectSiteVisible, setProjectSiteVisible] = useState<Graphic[]>([]);

  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
  const [initialExtent, setInitialExtent] = useState<Extent | null>(null);
  const [initialCenter, setInitialCenter] = useState<Point | null>();
  const [selState, setSelState] = useState("Active & Future");

  // console.log("Map", {
  //   config,
  //   area: { projectAreas, projectAreaVisible, selProjectAreas },
  //   sites: { projectSites, projectSiteVisible, selProjectSites },
  // });

  useEffect(() => {
    if (projectAreas && projectAreas.length > 0) {
      // setTimeout(() => {
      //   console.error({ projectAreas, first: projectAreas[0] });
      //   projectAreas[0].sidebarVisible = false;
      // }, 5000);
    }
  }, [projectAreas]);

  useEffect(() => {
    // Project Area Layer
    if (projectAreaLayer) {
      let expression = `OBJECTID IN (${projectAreaVisible.join(",")})`;
      if (selState === "Active & Future") {
        expression += ` AND (Status = 'Active' OR Status = 'Future')`;
        setSelProjectAreas(
          projectAreas.filter((project) =>
            ["Active", "Future"].includes(project.attributes.Status)
          )
        );
      } else if (selState !== "All") {
        expression += ` AND Status = '${selState}'`;
        setSelProjectAreas(
          projectAreas.filter(
            (project) => selState === project.attributes.Status
          )
        );
      } else {
        setSelProjectAreas(projectAreas);
      }
      // console.warn("projectAreaLayer", { expression });
      projectAreaLayer.definitionExpression = expression;
    }
  }, [projectAreas, projectAreaLayer, projectAreaVisible, selState]);

  useEffect(() => {
    // Project Sites Layer
    if (projectSiteLayer) {
      let expression = `OBJECTID IN (${projectSiteVisible.join(",")})`;
      if (selState === "Active & Future") {
        expression += ` AND (Status = 'Active' OR Status = 'Future')`;
        setSelprojectSites(
          projectSites.filter((project) =>
            ["Active", "Future"].includes(project.attributes.Status)
          )
        );
      } else if (selState !== "All") {
        expression += ` AND Status = '${selState}'`;
        setSelprojectSites(
          projectSites.filter(
            (project) => selState === project.attributes.Status
          )
        );
      } else {
        setSelprojectSites(projectSites);
      }
      // console.warn("projectSiteLayer", { expression });
      projectSiteLayer.definitionExpression = expression;
    }
  }, [projectSites, projectSiteLayer, projectSiteVisible, selState]);

  useEffect(() => {
    // Load ESRI's IdentifyManager and add the token
    esriId.registerToken({
      server: config.server_url,
      token: config.token,
    });

    // Get the Parcel from the URL
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const layer_type = urlParams.get("layer");
    const project_id = urlParams.get("id");

    // console.error({
    //   layer_type, project_id
    // })

    if (project_id) {
      // create the Query
      const queryObj = new Query();
      queryObj.returnGeometry = true;
      queryObj.outFields = ["*"];
      queryObj.where = `OBJECTID = ${project_id}`;

      // default to project areas
      let query_url = config.project_area_url;

      // give option to project sites
      if (layer_type === "project_sites") {
        query_url = config.project_site_url;
      }

      // console.warn({queryObj, query_url})

      query
        .executeQueryJSON(query_url, queryObj)
        .then((result) => {
          // console.log({ result });

          // Check if there is a result or not
          if (result.features.length === 1) {
            // console.warn(result.features);

            if (result.features[0].geometry.type === "point") {
              // setInitialCenter(result.features[0].geometry)
              setInitialCenter(
                new Point({
                  // @ts-ignore
                  x: result.features[0].geometry.x,
                  // @ts-ignore
                  y: result.features[0].geometry.y,
                  spatialReference: {
                    wkid: 3418,
                  },
                })
              );
              setInitialExtent(default_extent);
            } else {
              // Get the extent of the selected feature
              var ext = result.features[0].geometry.extent.clone().expand(1.25);
              ext.spatialReference.wkid = 3418;

              setInitialExtent(ext);
            }
          } else {
            setInitialExtent(default_extent);
          }
        })
        .catch((error) => {
          setInitialExtent(default_extent);
          console.error({ error });
        });
    } else {
      setInitialExtent(default_extent);
    }

    ReactGA.initialize(config.ga_token);
  }, []);

  useEffect(() => {
    if (initialExtent) {
      var basemap = new TileLayer({
        url: config.basemap_url,
        visible: true,
        id: "basemap",
        title: "basemap",
        // index: 0,
        minScale: 409600,
        maxScale: 200,
      });

      var sc = new TileLayer({
        url: config.sc_url,
        id: "Surrounding Counties",
        title: "Surrounding Counties",
        visible: true,
        minScale: 409600,
        maxScale: 200,
        // minZoom: 0,
        // maxZoom: 14,
        // index: 1,
      });

      const boundary_layer = new FeatureLayer({
        url: config.municipality_url,
        definitionExpression: "Name = 'Burlington'",
      });

      var road_labels = new TileLayer({
        url: config.road_url,
        id: "Road Labels",
        title: "Road Labels",
        visible: true,
        minScale: 409600,
        maxScale: 200,
        // minZoom: 0,
        // maxZoom: 14,
        // index: 1,
      });

      const template = {
        // autocasts as new PopupTemplate()
        title: "{ProjectName} Project - {OBJECTID}",
        content: (feature: Feature) => {
          var attr = feature.graphic.attributes;
          // console.log({ feature, attr });
          return `
            <a href="${attr.ProjectLink}"} target="_blank" class="ui primary fluid small button">
            <i class="external icon"></i>
            View Project
          </a>`;
        },
      };

      const project_area_layer = new FeatureLayer({
        // URL to the service
        url: config.project_area_url,
        popupEnabled: true,
        popupTemplate: template,
        outFields: ["*"],
      });

      const project_site_template = {
        // autocasts as new PopupTemplate()
        title: "{ProjectName} Project - {OBJECTID}",
        content: (feature: Feature) => {
          var attr = feature.graphic.attributes;
          // console.log({ feature, attr });
          return `
        <table class="esri-widget__table" summary="List of attributes and values">
          <tbody>
            <tr>
              <th style="width: 25%">ID</th>
              <td>${valueRender(attr.OBJECTID)}</td>
            </tr>
            <tr>
              <th style="width: 25%">Year</th>
              <td>${valueRender(attr.Year)}</td>
            </tr>
            <tr>
              <th style="width: 25%">Status</th>
              <td>${valueRender(attr.Status)}</td>
            </tr>
            <tr>
              <th style="width: 25%">Type</th>
              <td>${valueRender(attr.ProjectType)}</td>
            </tr>
            <tr>
              <th style="width: 25%">Description</th>
              <td>${valueRender(attr.ProjectDes)}</td>
            </tr>
          </tbody>
        </table>
        <hr />
        <a href="${
          attr.ProjectLink
        }"} target="_blank" class="ui primary fluid small button">
        <i class="external icon"></i>
        View Project
      </a>`;
        },
      };

      const project_site_layer = new FeatureLayer({
        // URL to the service
        url: config.project_site_url,
        popupEnabled: true,
        popupTemplate: project_site_template,
        outFields: ["*"],
      });

      let map = new Map({
        layers: [
          basemap,
          sc,
          boundary_layer,
          project_area_layer,
          project_site_layer,
          road_labels,
        ],
      });

      const mapViewObj = {
        container: "map-container",
        map: map,
        extent: initialCenter ? undefined : initialExtent,
        center: initialCenter ? initialCenter : undefined,
        zoom: initialCenter ? 6 : undefined,
        constraints: {
          rotationEnabled: false,
          snapToZoom: false,
          minScale: 409600,
          maxScale: 200,
          minZoom: 0,
          maxZoom: 11,
        },
      };

      let mapView = new MapView(mapViewObj);
      // mapView.popup.popupEnabled = true;
      mapView.popup.dockOptions = {
        buttonEnabled: false,
        // breakpoint: false,
      };
      mapView.popup.dockEnabled = false;
      mapView.popup.highlightEnabled = true;

      mapView.when(() => {
        setView(mapView);
        setProjectAreaLayer(project_area_layer);
        setProjectSiteLayer(project_site_layer);

        // mapView.whenLayerView(layer).then(function (layerView) {
        //   reactiveUtils.when(
        //     () => !layerView.updating,
        //     (val) => {
        //       layerView.queryFeatures().then(function (results) {
        //         console.log({ results }); // prints all the client-side features to the console

        //         results.features[0].visible = false;
        //         // layer.refresh();
        //       });
        //     }
        //   );
        // });

        // layer.queryFeatures().then(function (results) {
        //   // prints an array of all the features in the service to the console
        //   console.log({ queryFeatures: results.features });
        //   setProjectAreas(results.features);

        //   results.features[0].visible = false;
        //   layer.refresh();
        // });
        getProjects();
      });
    }
  }, [initialExtent, initialCenter]);

  const getProjects = () => {
    const queryObj = new Query();
    queryObj.returnGeometry = true;
    queryObj.outFields = ["*"];
    queryObj.orderByFields = ["ProjectName ASC"];
    queryObj.where = `1=1`;

    query
      .executeQueryJSON(config.project_area_url, queryObj)
      .then((result) => {
        // console.log({ result });
        setProjectAreas(result.features);
        setProjectAreaVisible(
          result.features.map((feature) => feature.attributes.OBJECTID)
        );
        // setYears([
        //   ...new Set(result.features.map((feature) => feature.attributes.Year)),
        // ]);
      })
      .catch((error) => {
        console.error({ error });
      });

    query
      .executeQueryJSON(config.project_site_url, queryObj)
      .then((result) => {
        // console.log({ result });
        setProjectSites(result.features);
        setProjectSiteVisible(
          result.features.map((feature) => feature.attributes.OBJECTID)
        );
        // setYears([
        //   ...new Set(result.features.map((feature) => feature.attributes.Year)),
        // ]);
      })
      .catch((error) => {
        console.error({ error });
      });
  };

  const valueRender = (value: any, date = false) => {
    if (date) {
      return value ? value : "NA";
    }
    return value ? value : "NA";
  };

  const zoomtoFeature = (feature: Graphic) => {
    if (!view) {
      return false;
    }

    if (feature.geometry.type === "point") {
      view.goTo({ center: feature.geometry, zoom: 12 });
    } else {
      // expand the extent and override the spatial reference because esri sucks
      var ext = feature.geometry.extent.clone().expand(1.25);
      ext.spatialReference.wkid = 3418;

      // Zoom to the extent
      view.goTo(ext);
    }
  };

  return (
    <>
      <Sidebar.Pushable
        as={Segment}
        id="site-pushable"
        attached
        style={{ height: "100%" }}
      >
        <Sidebar
          as={Segment}
          direction="right"
          animation="overlay"
          onHide={() => setSidebarVisible(false)}
          vertical
          visible={sidebarVisible}
          // width="large"
          style={{ textAlign: "left", backgroundColor: "#FFFFFF", padding: 0 }}
        >
          <div style={{ padding: "0.5em" }}>
            <Dropdown
              fluid
              button
              defaultValue="Active & Future"
              onChange={(_i, { value }) => {
                // @ts-ignore
                setSelState(value);
              }}
              options={[
                {
                  key: "All",
                  text: "All Projects",
                  value: "All",
                },
                {
                  key: "Active & Future",
                  text: "Active & Future",
                  value: "Active & Future",
                },
                {
                  key: "Active",
                  text: "Active",
                  value: "Active",
                },
                {
                  key: "Future",
                  text: "Future",
                  value: "Future",
                },
                {
                  key: "Completed",
                  text: "Completed",
                  value: "Completed",
                },
              ]}
            />
          </div>

          <Divider style={{ margin: "0 0.5em 0.5em" }} />

          <List divided style={{ padding: "0 .5em", margin: 0 }}>
            <Header as="h5" block inverted>
              Project Areas
            </Header>
            {selProjectAreas.length === 0 && (
              <Segment style={{ textAlign: "center" }}>
                No Projects match selected Status.
              </Segment>
            )}

            {selProjectAreas.map((project) => {
              // console.log({
              //   project,
              //   selState,
              //   nullit: selState != "All" && selState != project.attributes.Year,
              // });

              return (
                <List.Item
                  key={project.attributes.OBJECTID}
                  style={{ paddingBottom: ".5em", display: "flex" }}
                >
                  <Checkbox
                    label={project.attributes.ProjectName}
                    style={{ width: "100%", margin: "auto", flex: 1 }}
                    checked={projectAreaVisible.includes(
                      project.attributes.OBJECTID
                    )}
                    value={project.attributes.OBJECTID}
                    onClick={(_i, { checked, value }) => {
                      var ids = [...projectAreaVisible];
                      if (checked) {
                        // add to projectAreaVisible array
                        // @ts-ignore
                        ids.push(value);
                      } else {
                        // remove from the array
                        // @ts-ignore
                        ids = ids.filter((id) => id !== value);
                      }

                      setProjectAreaVisible(ids);
                    }}
                  />
                  <Button
                    icon="expand"
                    size="tiny"
                    onClick={() => zoomtoFeature(project)}
                  />
                </List.Item>
              );
            })}

            <Header as="h5" block inverted style={{ marginTop: ".5em" }}>
              Project Sites
            </Header>
            {selProjectSites.length === 0 && (
              <Segment style={{ textAlign: "center" }}>
                No Projects match selected Status.
              </Segment>
            )}

            {selProjectSites.map((project) => {
              // console.log({
              //   project,
              //   selState,
              //   nullit: selState != "All" && selState != project.attributes.Year,
              // });

              return (
                <List.Item
                  key={project.attributes.OBJECTID}
                  style={{ paddingBottom: ".5em", display: "flex" }}
                >
                  <Checkbox
                    label={project.attributes.ProjectName}
                    style={{ width: "100%", margin: "auto", flex: 1 }}
                    checked={projectSiteVisible.includes(
                      project.attributes.OBJECTID
                    )}
                    value={project.attributes.OBJECTID}
                    onClick={(_i, { checked, value }) => {
                      var ids = [...projectSiteVisible];
                      if (checked) {
                        // add to projectSiteVisible array
                        // @ts-ignore
                        ids.push(value);
                      } else {
                        // remove from the array
                        // @ts-ignore
                        ids = ids.filter((id) => id !== value);
                      }

                      setProjectSiteVisible(ids);
                    }}
                  />
                  <Button
                    icon="expand"
                    size="tiny"
                    onClick={() => zoomtoFeature(project)}
                  />
                </List.Item>
              );
            })}
          </List>
          {/* </Menu.item> */}
        </Sidebar>
        <Sidebar.Pusher id="site-pusher">
          <Segment id="site-container">
            <div id="map-container">
              {/* <div className="top-left map-buttons">
                {view && <ZoomWidget view={view} />}
              </div> */}
              <div className="top-right map-buttons">
                <Button onClick={() => setSidebarVisible(true)}>
                  <Icon name="list" />
                  Projects
                </Button>
              </div>
            </div>
          </Segment>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </>
  );
};

export default MapApp;
